import React, { useContext, useEffect, useRef, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import MatomoTracker from "./components/MatomoTracker";
import { ChatHistoryProvider } from "./store/ChatHistoryContext";
import {
  Dashboard, ResultScreen, Login, ResetPassword, PageNotFount, PlayerDashboard, GameDashboard, AdminDashboard, RequestTeam, Subscription, Addons, Checkout, TrialExpired, PaymentResult, SportAccess, TeamSeatOverflow, Profile, InvoiceStatus
} from "./pages";
import PrivacyPolicyBanner from "./components/PrivacyPolicyBanner";
import TermsAndConditionsModal from "./components/TermsAndConditionsModal";

import TeamDashboard from "./pages/TeamDashboard";

import {
  LOGIN,
  DASHBOARD,
  PROFILE_PAGE,
  RESETPASSWORD,
  RESULTSCREEN,
  PLAYERDASHBOARD,
  GAMEDASHBOARD,
  PAGENOTFOUND,
  HOME,
  VERIFY,
  VERIFY_ROUTE,
  SIGNUP,
  TERMS_AGREED,
  ADMIN_DASHBOARD,
  TEAMDASHBOARD,
  INFO,
  REQUEST_TEAM, SUBSCRIPTION, ADDONS, SPORT_ACCESS, TEAM_SEAT_OVERFLOW, CHECKOUT, INVOICE_STATUS, TRIAL_EXPIRED, PAYMENT_RESULT, SCHEDULE
} from "./constants/routes";
import SportAccessGuard from "./components/SportAccessGuard";
import Footer from "./components/Footer";
import Header from "./components/Header";
import ChatBot from "./components/ChatBot";
import ToastNotification from "./components/ToastNotification";
import { AuthContext } from "./store/ContextStore";
import localStorageService from './services/localStorageService';
import AuthHandler from "./utils/AuthHandler";
import api from "./utils/api";
import CreateUser from "./pages/SignUp"; // Choose your preferred solution|
import InfoPage from "./pages/InfoPage";
import Records from "./pages/Records";
import FullSchedule from "./pages/FullSchedule";
import { isTrialExpired, isSubscribed, isTrialExpiredForSport, normalizeUserFromSubscription } from "./services/trialService";

import TeamSeatOverflowModalManager from "./components/TeamSeatOverflowModalManager";
import eventEmitter from "./utils/eventEmitter";
import {
  PRIVACY_POLICY_ACCEPTED_KEY,
  PRIVACY_POLICY_VERSION_KEY,
  TERMS_ACCEPTED_KEY,
  TERMS_VERSION_KEY,
  CURRENT_POLICY_VERSION,
} from "./services/policyService";

function App() {
  const { isAuthenticated, setIsAuthenticated, selectedSport, setSelectedSport, setUser, setTeam, setIsModalClosed, user } = useContext(AuthContext);
  // const [showTermsModal, setShowTermsModal] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const lastTrialExpiredEmitAtRef = useRef(0);

  // Policy banner states
  const [showPrivacyBanner, setShowPrivacyBanner] = useState(false);
  const [privacyBannerCallback, setPrivacyBannerCallback] = useState<(() => void) | null>(null);

  // Check if the user has admin role
  const isAdmin = user?.isadmin === true;

  useEffect(() => {
    async function initAuth() {
      try {
        setAuthChecked(false);
        
        const storedUser = localStorageService.getUserInfo();
        
        if (storedUser && Object.keys(storedUser).length > 0) {
          const token = storedUser.access_token || '';
          
          // Set initial user state from localStorage
          setUser(storedUser);
          setIsAuthenticated(true);
          const storedSportCodes: string[] = Array.isArray((storedUser as any)?.sports)
            ? (storedUser as any).sports.map((s: any) => s?.code).filter(Boolean)
            : [];
          
          // Try to restore the previously selected sport from localStorage first
          const savedSport = localStorageService.getSelectedSport('');
          const initialSport = (savedSport && storedSportCodes.includes(savedSport))
            ? savedSport
            : (storedSportCodes.includes('MFB')
                ? 'MFB'
                : storedSportCodes[0] || '');
          
          if (initialSport) localStorageService.setSelectedSport(initialSport);
          setSelectedSport(initialSport);
          setTeam(storedUser.team || {});
          
          if (token) {
            try {
              // Verify token and get fresh user data
              const response = await api.get(VERIFY, {
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`
                }
              });
              
              // Use fresh user data from API response if available
              const freshUserData = normalizeUserFromSubscription(response.data?.user || storedUser);

              // Preserve privacy_terms from stored user if not in fresh data
              if (storedUser?.privacy_terms !== undefined && freshUserData?.privacy_terms === undefined) {
                freshUserData.privacy_terms = storedUser.privacy_terms;
              }

              // Preserve meta from verify response (contains seat_overflow flag)
              const responseMeta = (response.data?.user as any)?.meta;
              if (responseMeta !== undefined) {
                freshUserData.meta = responseMeta;
              }

              // Update state with fresh data
              setUser(freshUserData);
              setTeam(freshUserData.team || {});

              // Also update localStorage with fresh data (includes meta with seat_overflow)
              localStorageService.addUserInfo(freshUserData);
              
              // Check terms agreement with fresh data - only show modal if terms not agreed
              // if (freshUserData.terms_agreed === false) {
              //   setShowTermsModal(true);
              // }
              
            } catch (error) {
              console.error("Token verification failed:", error);
              // Token invalid or expired - keep the stored data but don't show terms modal
              // Let AuthHandler handle the auth verification
              console.log("Using stored user data due to verification error");
              
              // Still check terms agreement with stored data as fallback
              // const termsAgreed = localStorage.getItem("terms_agreed") || "false";
              // if (storedUser.terms_agreed === false && termsAgreed === "false") {
              //   setShowTermsModal(true);
              // }
            }
          } else {
            // No token but have user data - check terms with stored data
            // const termsAgreed = localStorage.getItem("terms_agreed") || "false";
            // if (storedUser.terms_agreed === false && termsAgreed === "false") {
            //   setShowTermsModal(true);
            // }
          }
        } else {
          // No stored user data
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error("Auth initialization error:", error);
        setIsAuthenticated(false);
      } finally {
        setAuthChecked(true);
      }
    }

    initAuth();
  }, [setIsAuthenticated, setSelectedSport, setUser, setTeam]);

  // Proactively show TrialExpired modal on login/refresh if any trial sport is expired.
  useEffect(() => {
    if (!isAuthenticated) return;
    const subscription = (user as any)?.subscription || localStorageService.getUserInfo()?.subscription;
    const sportCode = selectedSport || localStorageService.getSelectedSport('');
    if (!sportCode) return;
    if (!isTrialExpiredForSport(subscription, sportCode)) return;

    const now = Date.now();
    if (now - lastTrialExpiredEmitAtRef.current < 800) return;
    lastTrialExpiredEmitAtRef.current = now;

    eventEmitter.emit("trial_expired", {
      code: "TRIAL_EXPIRED",
      message: "Your trial has ended. Please upgrade to continue accessing our platform.",
    });
  }, [isAuthenticated, selectedSport, (user as any)?.subscription]);

  // Check for seat_overflow after user data is loaded from verify API
  const lastSeatOverflowEmitAtRef = useRef(0);
  useEffect(() => {
    if (!isAuthenticated) return;

    // Get currently selected sport
    const currentSportCode = selectedSport || localStorageService.getSelectedSport('');
    if (!currentSportCode) return;

    // Check subscription sport_access for seat_locked status - ONLY for the selected sport
    const userInfo = localStorageService.getUserInfo();
    const subscription = (userInfo as any)?.subscription;
    const sportAccess = subscription?.sport_access ?? subscription?.sportAccess;
    
    let hasOverflow = false;
    if (sportAccess && typeof sportAccess === 'object') {
      const selectedSportAccess = (sportAccess as any)[currentSportCode];
      // Only check for 'seat_locked', not 'locked' (locked is for payment/other issues handled by SportAccessGuard)
      hasOverflow = selectedSportAccess?.status === 'seat_locked';
    }

    if (hasOverflow) {
      const now = Date.now();
      // Debounce to prevent duplicate emissions
      if (now - lastSeatOverflowEmitAtRef.current > 800) {
        lastSeatOverflowEmitAtRef.current = now;
        eventEmitter.emit("team_seat_overflow", {
          code: "TEAM_SEAT_OVERFLOW",
          message:
            "Access is currently unavailable because your team has reached the maximum number of allowed users. Please contact your team administrator to request access.",
        });
      }
    }
  }, [isAuthenticated, user, selectedSport]);

  // Check if policy banners should be shown on auth change - REMOVED: no longer auto-show on login

  const handlePrivacyAccepted = () => {
    setShowPrivacyBanner(false);
    // Update user context to reflect acceptance
    if (user) {
      const updatedUser = { ...user, privacy_terms: true };
      setUser(updatedUser);
      localStorageService.addUserInfo(updatedUser);
    }
    if (privacyBannerCallback) {
      privacyBannerCallback();
      setPrivacyBannerCallback(null);
    }
  };

  // Listen for privacy banner show requests (from Checkout page) - KEPT for backward compatibility
  useEffect(() => {
    const handleShowPrivacyBanner = (callback?: () => void) => {
      if (callback) {
        setPrivacyBannerCallback(() => callback);
      }
      setShowPrivacyBanner(true);
    };

    eventEmitter.on('show_privacy_banner', handleShowPrivacyBanner);

    return () => {
      eventEmitter.off('show_privacy_banner', handleShowPrivacyBanner);
    };
  }, []);

  const appRoutes = [
    // Public routes - no authentication required
    { path: LOGIN, component: <Login />, isPublic: true },
    { path: SIGNUP, component: <CreateUser />, isPublic: true },
    { path: VERIFY_ROUTE, component: <Login />, isPublic: true },
    { path: HOME, component: <Login />, isPublic: true },
    { path: PAGENOTFOUND, component: <PageNotFount />, isPublic: true },
    { path: RESETPASSWORD, component: <ResetPassword />, isPublic: true },
    
    // Protected routes - require authentication
    { path: DASHBOARD, component: <Dashboard />, isPublic: false },
    { path: RESULTSCREEN, component: <ResultScreen />, isPublic: false },
    { path: `${PLAYERDASHBOARD}/:sportsCode/:playerId`, component: <PlayerDashboard/>, isPublic: false },
    { path: `${GAMEDASHBOARD}/:sportsCode/:matchId/:teamcode`, component: <GameDashboard/>, isPublic: false },
    // { path: `${INFO}`, component: <InfoPage />, isPublic: true },

    // Admin routes - require authentication and admin privileges
    { path: ADMIN_DASHBOARD, component: <AdminDashboard />, isPublic: false, isAdmin: true },
  ];

  // const handleAgree = async () => {
  //   try {
  //     setShowTermsModal(false);
  //     setIsModalClosed(true);

  //     const userInfo = localStorageService.getUserInfo();
  //     const token = userInfo.access_token || '';

  //     const res = await api.get(TERMS_AGREED, {
  //       headers: {
  //         "Content-Type": "application/json",
  //         Authorization: `Bearer ${token}`
  //       }
  //     });

  //     if (res.status >= 200 && res.status <= 210) {
  //       // Update user object with new terms_agreed status
  //       const updatedUser = { ...user, terms_agreed: true };
  //       setUser(updatedUser);
        
  //       // Update localStorage with fresh user data
  //       localStorageService.setUserInfo(updatedUser);
        
  //       // Also set the localStorage flag for backward compatibility
  //       localStorage.setItem("terms_agreed", "true");
  //     }

  //     console.log("res", res);
  //   } catch (error) {
  //     console.error("Error updating terms agreement:", error);
  //     // If API call fails, still set the local flag to prevent modal from showing again
  //     localStorage.setItem("terms_agreed", "true");
  //   }
  // };

  // const handleDisagree = () => {
  //   setShowTermsModal(false);
  //   localStorage.clear();
  //   setIsAuthenticated(false);
  //   setUser({});
  // };

  // AppLayout component to handle conditional header rendering
    function AppLayout() {
      const location = useLocation();
      const navigate = useNavigate();

    // Check if trial is expired and user is not subscribed
    const trialExpired = isTrialExpired() && !isSubscribed();

    // Allow subscription and checkout pages even when trial is expired (for upgrade flow)
    const isOnSubscriptionPage = location.pathname === SUBSCRIPTION;
    const isOnAddonsPage = location.pathname === ADDONS;
      const isOnCheckoutPage = location.pathname === CHECKOUT;
      const isOnInvoiceStatusPage = location.pathname === INVOICE_STATUS;
      const isOnPaymentResultPage = location.pathname === PAYMENT_RESULT;
      const isOnSportAccessPage = location.pathname === SPORT_ACCESS;
      const isOnTeamSeatOverflowPage = location.pathname === TEAM_SEAT_OVERFLOW;
      const canAccessSubscriptionPages = isOnSubscriptionPage || isOnAddonsPage || isOnCheckoutPage || isOnInvoiceStatusPage || isOnPaymentResultPage || isOnSportAccessPage || isOnTeamSeatOverflowPage;

    // Routes where header should NOT be shown even if authenticated
    const routesWithoutHeader = [RESETPASSWORD];

    // Check if current path should show header
    const shouldShowHeader = isAuthenticated && !routesWithoutHeader.some(route =>
      location.pathname === route || location.pathname.startsWith(route)
    );

    // Show ONLY TrialExpired page when trial is over - no other content accessible (except subscription/checkout)
    if (trialExpired && !canAccessSubscriptionPages) {
      return (
        <div className="bg-gradient-to-t from-[#E3E8F5] to-[#F6F9FC] flex flex-col min-h-screen">
          {/* Full header but TrialExpired page - header hides trial buttons automatically */}
          <Header trialExpired={trialExpired} />
          <main className="flex flex-grow w-full overflow-x-hidden items-center justify-center">
            <TrialExpired />
          </main>
        </div>
      );
    }

    return (
      <div className="bg-gradient-to-t from-[#E3E8F5] to-[#F6F9FC] flex flex-col min-h-screen">
        <ToastNotification />
        {/* Show full header when on subscription/checkout pages even during trial, otherwise use normal header */}
        {isOnSubscriptionPage || isOnAddonsPage || isOnCheckoutPage || isOnInvoiceStatusPage || isOnPaymentResultPage || isOnSportAccessPage || isOnTeamSeatOverflowPage ? (
          <Header trialExpired={trialExpired} />
        ) : (
          shouldShowHeader && <Header trialExpired={trialExpired} />
        )}
        {/* {showTermsModal && <TermsAndConditionsModal onAgree={handleAgree} />} */}
        <main className="flex flex-grow w-full overflow-x-hidden">
          <Routes>
            {/* Root path */}
            <Route
              path="/"
              element={
                trialExpired ? <Navigate to={TRIAL_EXPIRED} replace /> :
                isAuthenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />
              }
            />
            
            {/* Public Routes */}
            <Route path={LOGIN} element={isAuthenticated ? <Navigate to={DASHBOARD} replace /> : <Login />} />
            <Route path={SIGNUP} element={isAuthenticated ? <Navigate to={DASHBOARD} replace /> : <CreateUser />} />
            <Route path={VERIFY_ROUTE} element={<Login />} />
            <Route path={RESETPASSWORD} element={<ResetPassword />} />
            <Route path={PAGENOTFOUND} element={<PageNotFount />} />
            <Route path={REQUEST_TEAM} element={<RequestTeam />} />
            <Route path={SUBSCRIPTION} element={<Subscription />} />
            <Route path={ADDONS} element={<Addons />} />
            <Route path={SPORT_ACCESS} element={<SportAccess />} />
            <Route path={TEAM_SEAT_OVERFLOW} element={<TeamSeatOverflow />} />
            <Route path={CHECKOUT} element={<Checkout />} />
            <Route path={INVOICE_STATUS} element={<InvoiceStatus />} />
            <Route path={PAYMENT_RESULT} element={<PaymentResult />} />
            <Route path={TRIAL_EXPIRED} element={<TrialExpired />} />
            <Route path="/info" element={<InfoPage />} />

            
            {/* Protected Routes - Only accessible when authenticated */}
            <Route
              path={DASHBOARD}
              element={trialExpired ? <Navigate to={TRIAL_EXPIRED} replace /> : isAuthenticated ? <SportAccessGuard><Dashboard /></SportAccessGuard> : <Navigate to={LOGIN} replace />}
            />

            <Route
              path={PROFILE_PAGE}
              element={isAuthenticated ? <Profile /> : <Navigate to={LOGIN} replace />}
            />

            <Route path={`${SCHEDULE}/:teamName/:season`} element={trialExpired ? <Navigate to={TRIAL_EXPIRED} replace /> : isAuthenticated ?
              <SportAccessGuard><FullSchedule /></SportAccessGuard> : <Navigate to={LOGIN} replace />} />

            <Route
              path={RESULTSCREEN}
              element={trialExpired ? <Navigate to={TRIAL_EXPIRED} replace /> : isAuthenticated ? <SportAccessGuard><ResultScreen /></SportAccessGuard> : <Navigate to={LOGIN} replace />}
            />

            {/* Dynamic Routes - Explicitly defined */}
            <Route
              path={`${PLAYERDASHBOARD}/:sportsCode/:playerId`}
              element={trialExpired ? <Navigate to={TRIAL_EXPIRED} replace /> : isAuthenticated ? <SportAccessGuard><PlayerDashboard /></SportAccessGuard> : <Navigate to={LOGIN} replace />}
            />

              <Route
              path={`${TEAMDASHBOARD}/:sportsCode/:teamCode`}
              element={trialExpired ? <Navigate to={TRIAL_EXPIRED} replace /> : isAuthenticated ? <SportAccessGuard><TeamDashboard /></SportAccessGuard> : <Navigate to={LOGIN} replace />}
            />

            <Route
              path={`${GAMEDASHBOARD}/:sportsCode/:matchId/:teamcode`}
              element={trialExpired ? <Navigate to={TRIAL_EXPIRED} replace /> : isAuthenticated ? <SportAccessGuard><GameDashboard /></SportAccessGuard> : <Navigate to={LOGIN} replace />}
            />


            {/* Admin Routes */}
            <Route
              path={ADMIN_DASHBOARD}
              element={trialExpired ? <Navigate to={TRIAL_EXPIRED} replace /> : isAuthenticated && isAdmin ? <AdminDashboard /> : <Navigate to={DASHBOARD} replace />}
            />

            <Route path="/records" element={trialExpired ? <Navigate to={TRIAL_EXPIRED} replace /> : <SportAccessGuard><Records/></SportAccessGuard>} />

            {/* 404 route */}
            <Route path="*" element={<PageNotFount />} />

            {/* <Route path="/visualization" element={<VisualizationScreen />} /> */}



          </Routes>
        </main>
        <Footer />
        {/* ✅ Global ChatBot - Available on all pages for all users */}
        {
            isAuthenticated && !trialExpired && <ChatBot entity="Player" />
        }

     
      </div>
    );
  }

  // Show loading state while checking authentication
  if (!authChecked) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-t from-[#E3E8F5] to-[#F6F9FC] px-4">
        <div className="text-base sm:text-lg text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <Router>
      <ChatHistoryProvider>
        <AuthHandler/>
        <TeamSeatOverflowModalManager />
       
        {/* Matomo Analytics Tracker */}
        <MatomoTracker
          matomoUrl={process.env.REACT_APP_MATOMO_URL || 'https://matomo.athlytesports.com'}
          siteId={parseInt(process.env.REACT_APP_MATOMO_SITE_ID || '1')}
          userId={user?.username}
        />
        <AppLayout />

        {/* Policy Banners */}
        {showPrivacyBanner && (
          <PrivacyPolicyBanner onAccepted={handlePrivacyAccepted} onClose={() => setShowPrivacyBanner(false)} />
        )}
        {/* <ReportIssueModal /> */}
      </ChatHistoryProvider>
    </Router>
  );
}

export default App;
